import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

/**
 * Props for the Carousel component.
 */
export interface CarouselProps {
  /**
   * List of elements to render inside the carousel.
   */
  children: React.ReactNode[];

  /**
   * Optional CSS selector used to identify scrollable items inside the carousel.
   * Default is '*' which selects all direct children.
   */
  itemSelector?: string;
}

/**
 * A responsive, generic horizontal carousel with:
 * - hover-activated arrows (desktop only)
 * - fade-out overflow hint
 * - mobile touch-drag support
 * - smart scroll-to-next logic
 */
export const Carousel = ({ children, itemSelector = "*" }: CarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  /**
   * Updates visibility of scroll arrows based on scroll position.
   */
  const updateScrollVisibility = () => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    // Wait for next tick to ensure the container is mounted
    setTimeout(() => {
      updateScrollVisibility();
    }, 0);
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollVisibility);
    window.addEventListener("resize", updateScrollVisibility);

    return () => {
      el.removeEventListener("scroll", updateScrollVisibility);
      window.removeEventListener("resize", updateScrollVisibility);
    };
  }, []);

  /**
   * Scrolls to the next partially hidden item in the given direction.
   * @param direction Scroll direction: "left" or "right"
   */
  const scrollToNextItem = (direction: "left" | "right") => {
    const el = containerRef.current;
    if (!el) return;

    const items = Array.from(el.querySelectorAll<HTMLElement>(itemSelector));

    const scrollLeft = el.scrollLeft;
    const containerWidth = el.clientWidth;
    const buffer = 4; // buffer in pixels to prevent pixel rounding issues

    let target: HTMLElement | null = null;

    if (direction === "right") {
      for (const item of items) {
        const itemLeft = item.offsetLeft;
        const itemRight = itemLeft + item.offsetWidth;

        // If right edge of item is beyond the current view
        if (itemRight > scrollLeft + containerWidth + buffer) {
          target = item;
          break;
        }
      }

      // If nothing found, scroll to the far end
      if (!target) {
        el.scrollTo({
          left: el.scrollWidth,
          behavior: "smooth",
        });
        return;
      }
    }

    if (direction === "left") {
      // Iterate in reverse
      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        const itemLeft = item.offsetLeft;

        // If left edge is before current scroll position
        if (itemLeft < scrollLeft - buffer) {
          target = item;
          break;
        }
      }

      // If nothing found, scroll to far left
      if (!target) {
        el.scrollTo({
          left: 0,
          behavior: "smooth",
        });
        return;
      }
    }

    if (target) {
      // Scroll to the target item’s left edge
      el.scrollTo({
        left: target.offsetLeft,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Fade overlays */}
      {/* Left overlay */}
      {canScrollLeft && isHovered && (
        <div className="block pointer-events-none absolute -left-4 top-0 bottom-0 w-12 bg-linear-to-r from-bg-base to-transparent z-10" />
      )}
      {/* Right overlay */}
      {canScrollRight && isHovered && (
        <div className="block pointer-events-none absolute -right-4 top-0 bottom-0 w-12 bg-linear-to-l from-bg-base to-transparent z-10" />
      )}

      {/* Arrows */}
      {/* Left arrow */}
      {canScrollLeft && isHovered && (
        <button
          onClick={() => scrollToNextItem("left")}
          className="hidden sm:flex items-center justify-center absolute -left-2 top-1/2 -translate-y-1/2 z-20 p-1 rounded-full "
          aria-label="Scroll Left"
        >
          <ChevronLeft size={20} className="text-fg-muted" />
        </button>
      )}

      {/* Right arrow */}
      {canScrollRight && isHovered && (
        <button
          onClick={() => scrollToNextItem("right")}
          className="hidden sm:flex items-center justify-center absolute -right-2 top-1/2 -translate-y-1/2 z-20 p-1 rounded-full "
          aria-label="Scroll Right"
        >
          <ChevronRight size={20} className="text-fg-muted" />
        </button>
      )}

      {/* Scrollable container */}
      <div
        className="overflow-x-auto flex space-x-2 scroll-smooth scrollbar-hide px-6 -mx-6 py-px"
        ref={containerRef}
      >
        {children}
      </div>
    </div>
  );
};
